import { IconInfoCircle } from '@tabler/icons-react';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { Fragment } from 'react';
import Accordion from '@/components/content-types/accordion';
import { isBlockEditorEmpty } from '@/app/utils';
import { cn } from '@/app/utils/cn';
import checkTruthyValue from '@/app/utils/checkTruthyValue';

const colorAsterisk = (text) => {
    if (!text) return null;
    if (text.endsWith('*')) {
        const textWithoutAsterisk = text.slice(0, -1);
        const asterisk = text.slice(-1);

        return (
            <>
                <span>{textWithoutAsterisk}</span>
                <span className="text-orange">{asterisk}</span>
            </>
        );
    } else {
        return text;
    }
};

const marker = () => {
    return <div className="inline-block px-2">•</div>;
};

const emptyHeaderCell = (getWidthFn) => {
    return (
        <th className={getWidthFn()}>
            <div></div>
        </th>
    );
};

const emptyDataCell = (getWidthFn) => {
    return (
        <td className={getWidthFn()}>
            <div></div>
        </td>
    );
};

const sanitizeLabel = (label) => (label === '&nbsp;' ? '' : label);

const getBooleanValue = (value) => {
    if (Array.isArray(value)) {
        return false;
    }
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return Boolean(value);
};

const getActiveColumnsCount = (
    columnLabelOne,
    columnLabelTwo,
    columnLabelThree,
    columnLabelFour
) => {
    return [columnLabelOne, columnLabelTwo, columnLabelThree, columnLabelFour].filter(Boolean)
        .length;
};

const mostPopular = () => {
    return (
        <div className="absolute left-1/2 top-[-14px] z-50 h-[32px] w-[40px] -translate-x-1/2">
            <div className="rounded-md bg-fuchsia-700 text-center text-base">🌟</div>
        </div>
    );
};

function FeatureTable(props) {
    const {
        idForAnchor,
        tableFeatures,
        isAccordionTable,
        accordionFeatures,
        cellName,
        columnLabelOne,
        columnLabelTwo,
        columnLabelThree,
        columnLabelFour,
        markedAsList,
        mostPopularColumn,
        hideTitleColumn,
        alignCellCenter,
        showDescriptionOnly = 'false',
        removeLeftSpace,
        firstColumnWidth,
        secondColumnWidth,
        thirdColumnWidth,
        fourthColumnWidth,
        autoSetWidthForColumns,
        emptyCellWidth,
        columnWidthMultiplier = 1
    } = props;
    const descriptionOnly = getBooleanValue(showDescriptionOnly);
    const alignCenter = getBooleanValue(alignCellCenter);
    const leftSpace = getBooleanValue(removeLeftSpace);
    const emptyCellWidthBool = getBooleanValue(emptyCellWidth);

    const columnSize = getActiveColumnsCount(
        columnLabelOne,
        columnLabelTwo,
        columnLabelThree,
        columnLabelFour
    );

    const isSingleColumnTable = columnSize === 1;

    const emptyClass = cn(leftSpace ? 'pr-4' : 'pl-[6%] pr-4');

    const emptySpaceClass = cn(!isSingleColumnTable ? emptyClass : 'pl-4');
    const topSpaceClass = cn(isSingleColumnTable && '!mt-8 md:w-[50%] mx-auto !text-base');

    const tableDataClass = cn(
        `${alignCenter ? 'text-center' : `${emptySpaceClass} h-16 py-2`}`,
        `${isSingleColumnTable ? 'text-base' : ''} `
    );
    const tableHeaderClass = cn(
        `${alignCenter ? 'text-center' : `${emptySpaceClass}`}`,
        `${isSingleColumnTable ? 'text-base' : ''} `
    );
    const hoverClass = `[&:hover>*:last-child]:visible [&:hover>*:last-child]:opacity-100`;
    const informerPositionClass = `invisible absolute w-1/2 translate-x-2 translate-y-1/2 px-2`;

    if (accordionFeatures && accordionFeatures.length > 0) {
        const tableHeader = {
            mostPopularColumn,
            alignCellCenter,
            markedAsList,
            cellName,
            columnLabelOne,
            columnLabelTwo,
            columnLabelThree,
            columnLabelFour,
            firstColumnWidth,
            secondColumnWidth,
            thirdColumnWidth,
            fourthColumnWidth,
            autoSetWidthForColumns,
            emptyCellWidth,
            idForAnchor
        };

        return (
            <div className="container bg-white px-0">
                <Accordion
                    tableHeader={tableHeader}
                    featuretablecells={accordionFeatures}
                    layout={2}
                />
            </div>
        );
    }

    const getColumnWidth = (columnIndex) => {
        if (getBooleanValue(autoSetWidthForColumns)) {
            const totalColumns = columnSize + (emptyCellWidthBool ? 2 : 0);

            if (totalColumns > 0) {
                const equalWidth = 100 / totalColumns;
                return cn(`md:w-[${Math.round(equalWidth)}%]`);
            }
        }

        const widthMap = {
            1: firstColumnWidth,
            2: secondColumnWidth,
            3: thirdColumnWidth,
            4: fourthColumnWidth
        };

        const width = widthMap[columnIndex];
        if (width && width > 0 && width < 101) {
            return cn(`md:w-[${width}%]`);
        }

        const defaultWidths = {
            1: 'w-1/5',
            2: 'w-[41%]',
            3: 'w-[29%]',
            4: 'w-[19%]'
        };

        return defaultWidths[columnIndex] || 'w-auto';
    };

    const getEmptyCellWidth = () => {
        if (getBooleanValue(autoSetWidthForColumns)) {
            if (columnSize > 0) {
                const totalColumns = columnSize + 2;

                if (columnWidthMultiplier && +columnWidthMultiplier > 0) {
                    const equalWidth = 100 / totalColumns / +columnWidthMultiplier;

                    if (isFinite(equalWidth) && equalWidth > 0) {
                        return cn(`md:w-[${Math.round(equalWidth)}%]`);
                    }
                }
            }
        }

        if (typeof emptyCellWidth === 'number' && emptyCellWidth > 0 && emptyCellWidth < 101) {
            return cn(`md:w-[${emptyCellWidth}%]`);
        }

        if (typeof emptyCellWidth === 'string' && emptyCellWidth.includes('%')) {
            return cn(`md:w-[${emptyCellWidth}]`);
        }

        return 'w-4 md:w-14';
    };

    const tableLayouts = () => {
        // has 5 column
        if (cellName && isAccordionTable && columnLabelFour)
            return {
                tableHeader: `${alignCenter ? 'text-center' : 'pr-4 pl-[1%]'} ${getColumnWidth(4)}`,
                tableData: tableDataClass
            };
        // has 4 column
        if (columnLabelFour)
            return {
                tableHeader: `${tableHeaderClass} ${getColumnWidth(4)}`,
                tableData: tableDataClass
            };
        // has 3 column
        if (columnLabelThree)
            return {
                tableHeader: `${tableHeaderClass} ${getColumnWidth(3)}`,
                tableData: tableDataClass
            };
        // has 2 column
        return {
            tableHeader: `${tableHeaderClass} ${getColumnWidth(2)}`,
            tableData: tableDataClass
        };
    };

    const columHeaderClass = cn(
        `relative border-blue-550 rotate-[315deg] text-[9px]`,
        `md:text-base sm:rotate-0 font-normal md:border-l`,
        `${tableLayouts().tableHeader}`
    );
    const columnClass = cn(
        `relative sm:text-base text-[9px] font-normal`,
        `${isAccordionTable ? 'border-indigo-200 md:border-l' : ''}`,
        `${tableLayouts().tableData}`
    );

    return (
        <table
            {...(idForAnchor ? { id: idForAnchor } : {})}
            className={cn('container relative w-full table-fixed', topSpaceClass)}>
            {!isAccordionTable && (
                <thead className="bg-blue-700 lg:text-xl">
                    <tr className="relative h-16 text-white sm:text-left md:h-[5.375rem]">
                        {!isSingleColumnTable && emptyHeaderCell(getEmptyCellWidth)}
                        {isSingleColumnTable && cellName && (
                            <th scope="col">{cellName == '&nbsp;' ? '' : cellName}</th>
                        )}
                        {cellName && cellName !== '&nbsp;' && (
                            <th
                                role="columnheader"
                                scope="col"
                                className={cn(
                                    `w-1/5 border-blue-550 rotate-[315deg] text-[9px]`,
                                    `sm:rotate-0 md:px-4 md:text-base font-normal md:border-r`,
                                    `${isSingleColumnTable && 'text-base'}`
                                )}>
                                {cellName == '&nbsp;' ? '' : cellName}
                            </th>
                        )}
                        {columnLabelOne && (
                            <th
                                role="columnheader"
                                scope="col"
                                className={cn(
                                    `relative !w-auto`,
                                    `${cellName ? tableLayouts().tableHeader : ''}`,
                                    `rotate-[315deg] text-[9px] font-normal sm:rotate-0 md:px-4 md:text-base`,
                                    `${isSingleColumnTable && 'text-[7px] sm:text-2xl sm:px-4'}`
                                )}>
                                {mostPopularColumn == 1 && mostPopular()}
                                {sanitizeLabel(columnLabelOne)}
                                {cellName == '&nbsp;' && ''}
                            </th>
                        )}
                        {[columnLabelTwo, columnLabelThree, columnLabelFour].map((label, index) => {
                            return label && (!descriptionOnly || index === 0) ? (
                                <th
                                    role="columnheader"
                                    scope="col"
                                    key={index}
                                    className={cn(columHeaderClass, 'w-auto')}>
                                    {mostPopularColumn == index + 2 && mostPopular()}
                                    {sanitizeLabel(label)}
                                </th>
                            ) : null;
                        })}
                        {!isSingleColumnTable && emptyHeaderCell(getEmptyCellWidth)}
                    </tr>
                </thead>
            )}
            <tbody className={`${isAccordionTable ? '!md:text-base' : 'md:text-sm'}`}>
                {tableFeatures?.map(
                    (
                        {
                            title,
                            columnValueOne,
                            columnValueTwo,
                            columnValueThree,
                            columnLabelFour,
                            description
                        },
                        index
                    ) => (
                        <Fragment key={index}>
                            <tr className="hidden">
                                <td className="h-2 opacity-0"></td>
                            </tr>
                            <tr
                                className={cn(
                                    'table-row-content text-left',
                                    isAccordionTable ? 'border-indigo-200 md:border-t' : '',
                                    isAccordionTable && index % 2 === 0
                                        ? 'bg-indigo-100'
                                        : 'bg-white'
                                )}>
                                {!isSingleColumnTable && emptyDataCell(getEmptyCellWidth)}
                                {!checkTruthyValue(hideTitleColumn) && (
                                    <td
                                        className={cn(
                                            'relative sm:gap py-2 sm:py-6 sm:gap-3 leading-[11px]',
                                            `${!index ? 'pt-2 sm:pt-4' : ''}`,
                                            `${getColumnWidth(columnSize)}`
                                        )}>
                                        <span className="align-middle text-[9px] leading-[11px] sm:text-base">
                                            {checkTruthyValue(markedAsList) && marker()}
                                        </span>
                                        <span className="align-middle text-[9px] leading-[11px] sm:text-base">
                                            {title}
                                        </span>
                                        {!isBlockEditorEmpty(description) && (
                                            <div className="inline-block align-middle sm:ml-3">
                                                <div
                                                    className={`flex cursor-pointer flex-col justify-center ${hoverClass} [&_p]:text-white`}>
                                                    <IconInfoCircle className="ml-1 size-[12px] stroke-1 text-primary sm:size-[24px] md:ml-0" />

                                                    <div
                                                        className={`${informerPositionClass} z-50 opacity-0 transition-all duration-200 sm:w-2/5`}>
                                                        <DotBlockEditor
                                                            blocks={description}
                                                            className="min-w-[160px] rounded-md bg-custom-steel p-2 text-sm text-white drop-shadow-md"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                )}

                                {columnLabelOne &&
                                    tableFeatures?.some((feature) => feature.columnValueOne) && (
                                        <td className={cn(columnClass, getColumnWidth(columnSize))}>
                                            {checkTruthyValue(markedAsList) && marker()}
                                            {sanitizeLabel(columnValueOne) || ''}
                                        </td>
                                    )}
                                {!descriptionOnly &&
                                    [columnValueTwo, columnValueThree, columnLabelFour].map(
                                        (columnValue, index) =>
                                            columnValue ? (
                                                <td
                                                    key={index}
                                                    className={cn(
                                                        columnClass,
                                                        getColumnWidth(columnSize)
                                                    )}>
                                                    {checkTruthyValue(markedAsList) && marker()}
                                                    {sanitizeLabel(colorAsterisk(columnValue))}
                                                </td>
                                            ) : null
                                    )}
                                {!isSingleColumnTable && emptyDataCell(getEmptyCellWidth)}
                            </tr>
                        </Fragment>
                    )
                )}
            </tbody>
        </table>
    );
}

export default FeatureTable;
